/**
 * Phase 96 — RelationalSelect
 * A dropdown selector bound to a relational entity collection
 * (centers / courses / batches / departments).
 *
 * Replaces hard-coded free-text dropdown options with a live list
 * fetched from the academy's Settings-managed entities.
 *
 * Highlights:
 *  - Inline "+ Add New" trigger inside the dropdown opens a lightweight popover
 *    that POSTs to /api/academy/{collection} and refreshes the option list
 *    without forcing the user to leave the parent form.
 *  - Popover is hoisted OUT of <SelectContent> so it survives Select-close
 *    unmounting (fix from Phase 96 testing report iteration_35).
 *  - PopoverAnchor wraps the Select so the popover anchors to the field.
 *  - Optional cascading via `filterFn(option) => bool`.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function RelationalSelect({
  value,
  onValueChange,
  endpoint,                 // e.g. '/academy/courses'
  entityLabel,              // 'Course'
  placeholder,
  filterFn,                 // optional (opt) => bool
  disabled = false,
  emptyHint,                // text shown when list is empty
  extraCreatePayload = {},  // forwarded into POST body (e.g. course_id when adding a batch)
  testid,
  onOptionsLoaded,          // optional (options) => void
  className,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint);
      const list = Array.isArray(r.data) ? r.data : [];
      setOptions(list);
      if (onOptionsLoaded) onOptionsLoaded(list);
    } catch (err) {
      console.error(`${entityLabel} fetch failed`, err);
    } finally {
      setLoading(false);
    }
  }, [endpoint, entityLabel, onOptionsLoaded]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  const visibleOptions = useMemo(() => {
    return filterFn ? options.filter(filterFn) : options;
  }, [options, filterFn]);

  // Phase 96 — Manually resolve the selected option's display label.
  // Radix <SelectValue/> derives its label from a mounted matching <SelectItem>'s
  // textContent, which only mounts while the Select is OPEN. After inline-create,
  // the Select is closed, so the new label was never captured and Radix falls back
  // to placeholder. Resolving manually fixes that race for good.
  const selectedOption = useMemo(
    () => options.find((o) => o.id === value),
    [options, value]
  );

  const handleOpenAddNew = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    // Close the Select first, then open the Popover in the next tick so
    // Radix has a chance to register the open transition cleanly.
    setSelectOpen(false);
    setTimeout(() => setPopoverOpen(true), 30);
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) { toast.error(`${entityLabel} name is required`); return; }
    setCreating(true);
    try {
      const payload = { name: trimmed, ...extraCreatePayload };
      const r = await api.post(endpoint, payload);
      toast.success(`${entityLabel} "${r.data.name}" created`);
      setNewName('');
      setPopoverOpen(false);
      // Optimistic local update so Radix Select has a matching <SelectItem> rendered
      // when we flip the bound value below.
      setOptions((prev) => (prev.find((o) => o.id === r.data.id) ? prev : [...prev, r.data]));
      // CRITICAL: Radix Select fires onValueChange('') when its bound value points
      // at a SelectItem whose React node is NOT currently mounted. SelectContent
      // (and therefore its SelectItem children) only mount while the dropdown is
      // OPEN. To make the new value stick, we briefly open the Select so Radix
      // registers the new SelectItem, then close it. The open->close happens
      // in the same animation frame and is visually imperceptible.
      setSelectOpen(true);
      // Wait one paint so SelectContent mounts.
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      if (r.data?.id && onValueChange) onValueChange(r.data.id);
      // Wait another tick so Radix can settle, then close.
      await new Promise((resolve) => setTimeout(resolve, 50));
      setSelectOpen(false);
      // Background full re-sync (covers other tabs / multi-window consistency).
      fetchOptions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to create ${entityLabel}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={className}>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverAnchor asChild>
          <div className="w-full">
            <Select
              value={value || ''}
              onValueChange={onValueChange}
              disabled={disabled}
              open={selectOpen}
              onOpenChange={setSelectOpen}
            >
              <SelectTrigger data-testid={testid}>
                {selectedOption ? (
                  <span className="truncate">{selectedOption.name}</span>
                ) : (
                  <SelectValue placeholder={placeholder || `Select ${entityLabel.toLowerCase()}`} />
                )}
              </SelectTrigger>
              <SelectContent>
                {loading && (
                  <div className="px-2 py-3 text-xs text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading {entityLabel.toLowerCase()}s…
                  </div>
                )}
                {!loading && visibleOptions.length === 0 && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">
                    {emptyHint || `No ${entityLabel.toLowerCase()}s configured yet.`}
                  </div>
                )}
                {!loading && visibleOptions.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id} data-testid={`${testid}-option-${opt.id}`}>
                    {opt.name}
                  </SelectItem>
                ))}
                <SelectSeparator />
                {/* Inline quick-create trigger. onClick closes the Select and opens the
                    hoisted Popover (rendered as a sibling so it survives SelectContent unmount). */}
                <button
                  type="button"
                  data-testid={`${testid}-add-new`}
                  onClick={handleOpenAddNew}
                  className="relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 px-2 text-sm font-medium text-primary hover:bg-primary/10 outline-none"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add new {entityLabel.toLowerCase()}
                </button>
              </SelectContent>
            </Select>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          className="w-72 p-3"
          onOpenAutoFocus={(e) => {
            // Let our autoFocus on the input take over the focus dance.
            e.preventDefault();
          }}
        >
          {/*
            NOTE: We deliberately do NOT use a <form> wrapper here.
            The popover content is portaled into document.body, but synthetic
            React events still bubble through the React tree to any ancestor
            <form>. When the parent (StudentForm / EmployeeForm) wraps fields
            in <form onSubmit={handleAddX}>, the inner form's submit event
            bubbles up and triggers the outer onSubmit, which can:
              (a) create a half-empty record (POST /students with empty name),
              (b) reset newStudent state, wiping our freshly set FK.
            Using a <div> + button-onClick + Enter-keydown avoids both issues.
          */}
          <div className="space-y-2.5" data-testid={`${testid}-add-new-form`}>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Quick-add {entityLabel}</Label>
              <Input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreate();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setPopoverOpen(false);
                  }
                }}
                placeholder={`${entityLabel} name`}
                data-testid={`${testid}-add-new-input`}
                className="h-8 text-sm"
              />
            </div>
            <div className="flex justify-end gap-1.5">
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs"
                onClick={() => { setNewName(''); setPopoverOpen(false); }}>
                Cancel
              </Button>
              <Button type="button" size="sm" className="h-7 px-3 text-xs"
                disabled={creating || !newName.trim()}
                onClick={handleCreate}
                data-testid={`${testid}-add-new-submit`}>
                {creating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                {creating ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
